/**
 * RTK Query API Service
 * Centralized API configuration for all backend calls
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  SoftwareProduct,
  SoftwareVersion,
  CVE,
  Job,
  JobLog,
  User,
  ApiKey,
  Notification,
  GroupedVersionsResponse,
} from '@patch-manager/shared';

// Store current token (updated by TokenSync)
let currentToken: string | null = null;

export function setCurrentToken(token: string | null) {
  currentToken = token;
  console.log('[API] Token updated, length:', token?.length || 0);
}

export function getCurrentToken(): string | null {
  return currentToken;
}

// API base URL
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// API Response types
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export interface SoftwareListParams {
  category?: string;
  manufacturer?: string[];
  is_open_source?: boolean;
  has_cves?: boolean;
  search?: string;
  type?: 'application' | 'os' | 'package';
  package_type?: 'rpm' | 'deb' | 'apk' | 'pacman';
  sort_by?: 'name' | 'manufacturer' | 'version_count' | 'cve_count';
  sort_order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface ManufacturerListItem {
  manufacturer: string;
  product_count: number;
}

export interface ReleaseChannel {
  release_channel: string;
  version_count: number;
}

export interface VersionListParams {
  software_id: number;
  os_platform?: string;
  architecture?: string;
  is_latest?: boolean;
  release_channel?: string;
  page?: number;
  limit?: number;
}

export interface JobListParams {
  status?: 'pending' | 'running' | 'completed' | 'failed';
  software_id?: number;
  page?: number;
  limit?: number;
}

// Custom base query that manually adds Authorization header
const customBaseQuery: ReturnType<typeof fetchBaseQuery> = async (args, _api, _extraOptions) => {
  console.log('[Custom baseQuery] Called with args:', args);

  // Build the request init
  const requestInit: RequestInit = {
    method: typeof args === 'string' ? 'GET' : args.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  };

  // Add Authorization header if token exists
  if (currentToken) {
    console.log('[Custom baseQuery] Adding Authorization header');
    (requestInit.headers as Record<string, string>)['Authorization'] = `Bearer ${currentToken}`;
  } else {
    console.warn('[Custom baseQuery] No token available');
  }

  // Add body if present
  if (typeof args !== 'string' && args.body) {
    requestInit.body = JSON.stringify(args.body);
  }

  // Build URL with query params
  let url: string;
  if (typeof args === 'string') {
    url = `${API_BASE_URL}${args}`;
  } else {
    url = `${API_BASE_URL}${args.url}`;
    // Append query params if present
    if (args.params) {
      const searchParams = new URLSearchParams();
      Object.entries(args.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
  }

  console.log('[Custom baseQuery] Fetching:', url);
  console.log('[Custom baseQuery] Headers:', requestInit.headers);

  // Make the request
  try {
    const response = await fetch(url, requestInit);
    console.log('[Custom baseQuery] Response status:', response.status);

    const data = await response.json();

    if (!response.ok) {
      return { error: { status: response.status, data } };
    }

    return { data };
  } catch (error) {
    console.error('[Custom baseQuery] Fetch error:', error);
    return { error: { status: 'FETCH_ERROR', error: String(error) } };
  }
};

// Define the API
export const api = createApi({
  reducerPath: 'api',
  baseQuery: customBaseQuery as any,
  tagTypes: ['Software', 'Version', 'CVE', 'Job', 'Notification', 'User', 'ApiKey', 'EmailPreferences', 'GlobalEmailSettings'],
  endpoints: (builder) => ({
    // Software endpoints
    getSoftwareList: builder.query<PaginatedResponse<SoftwareProduct>, SoftwareListParams | void>({
      query: (params) => {
        if (!params) {
          return { url: '/software', params: {} };
        }

        // Convert manufacturer array to comma-separated string
        const { manufacturer, ...restParams } = params;
        const queryParams: any = { ...restParams };

        if (manufacturer && manufacturer.length > 0) {
          queryParams.manufacturer = manufacturer.join(',');
        }

        return {
          url: '/software',
          params: queryParams,
        };
      },
      providesTags: ['Software'],
    }),

    getSoftwareById: builder.query<SoftwareProduct, number>({
      query: (id) => `/software/${id}`,
      transformResponse: (response: { data: SoftwareProduct }) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'Software', id }],
    }),

    getManufacturers: builder.query<{ data: ManufacturerListItem[] }, void>({
      query: () => '/software/manufacturers',
      providesTags: ['Software'],
    }),

    getReleaseChannels: builder.query<{ data: ReleaseChannel[] }, number>({
      query: (softwareId) => `/software/${softwareId}/release-channels`,
      providesTags: (_result, _error, softwareId) => [{ type: 'Software', id: softwareId }],
    }),

    // Version endpoints
    getVersionsBySoftware: builder.query<{ data: SoftwareVersion[] }, VersionListParams>({
      query: ({ software_id, ...params }) => ({
        url: `/software/${software_id}/versions`,
        params: { per_page: 1000, ...params }, // Request up to 1000 versions
      }),
      providesTags: (_result, _error, { software_id }) => [{ type: 'Version', id: software_id }],
    }),

    getGroupedVersionsBySoftware: builder.query<
      GroupedVersionsResponse,
      { software_id: number; page?: number; per_page?: number; release_channel?: string }
    >({
      query: ({ software_id, page = 1, per_page = 25, release_channel }) => ({
        url: `/software/${software_id}/versions/grouped`,
        params: { page, per_page, ...(release_channel && { release_channel }) },
      }),
      providesTags: (_result, _error, { software_id }) => [
        { type: 'Version', id: `SOFTWARE_${software_id}` },
      ],
    }),

    getLatestVersion: builder.query<SoftwareVersion, { software_id: number; os_platform: string; architecture: string }>({
      query: ({ software_id, os_platform, architecture }) => ({
        url: `/software/${software_id}/versions/latest`,
        params: { os_platform, architecture },
      }),
      providesTags: (_result, _error, { software_id }) => [{ type: 'Version', id: `${software_id}-latest` }],
    }),

    getVersionById: builder.query<SoftwareVersion, number>({
      query: (id) => `/versions/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Version', id }],
    }),

    getVersionDownloadUrl: builder.query<{ url: string; expires_at: string }, number>({
      query: (id) => `/versions/${id}/download`,
    }),

    // CVE endpoints
    getCVEsBySoftware: builder.query<CVE[], number>({
      query: (software_id) => ({
        url: '/cves',
        params: { software_id },
      }),
      transformResponse: (response: { data: CVE[] }) => response.data,
      providesTags: (_result, _error, software_id) => [{ type: 'CVE', id: software_id }],
    }),

    getCVEsByVersion: builder.query<CVE[], number>({
      query: (version_id) => `/versions/${version_id}/cves`,
      providesTags: (_result, _error, version_id) => [{ type: 'CVE', id: `version-${version_id}` }],
    }),

    searchCVEs: builder.query<PaginatedResponse<CVE>, { query?: string; severity?: string; page?: number }>({
      query: (params) => ({
        url: '/cves',
        params,
      }),
      providesTags: ['CVE'],
    }),

    // Job endpoints (admin only)
    getJobs: builder.query<PaginatedResponse<Job>, JobListParams | void>({
      query: (params) => ({
        url: '/jobs',
        params: params || {},
      }),
      providesTags: ['Job'],
    }),

    getJobById: builder.query<Job, number>({
      query: (id) => `/jobs/${id}`,
      transformResponse: (response: { data: Job }) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'Job', id }],
    }),

    getJobLogs: builder.query<JobLog[], number>({
      query: (job_id) => `/jobs/${job_id}/logs`,
      transformResponse: (response: { data: JobLog[] }) => response.data,
      providesTags: (_result, _error, job_id) => [{ type: 'Job', id: `logs-${job_id}` }],
    }),

    triggerJob: builder.mutation<{ job_id: number }, { software_id: number }>({
      query: (body) => ({
        url: '/jobs',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Job'],
    }),

    // User endpoints
    getCurrentUser: builder.query<User, void>({
      query: () => '/users/me',
      providesTags: ['User'],
    }),

    updateUserPreferences: builder.mutation<User, Partial<User>>({
      query: (body) => ({
        url: '/users/me',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['User'],
    }),

    // Email notification endpoints
    toggleEmailNotifications: builder.mutation<{ success: boolean; data: { preferences: any } }, boolean>({
      query: (enabled) => ({
        url: '/users/me/email-notifications',
        method: 'PATCH',
        body: { enabled },
      }),
      invalidatesTags: ['EmailPreferences', 'User'],
    }),

    getEmailPreferences: builder.query<{ success: boolean; data: { email: boolean } }, void>({
      query: () => '/users/me/email-preferences',
      providesTags: ['EmailPreferences'],
    }),

    // Admin email notification endpoints
    toggleGlobalEmailNotifications: builder.mutation<{ success: boolean; data: { enabled: boolean } }, boolean>({
      query: (enabled) => ({
        url: '/admin/email-notifications/global',
        method: 'PATCH',
        body: { enabled },
      }),
      invalidatesTags: ['GlobalEmailSettings'],
    }),

    getGlobalEmailNotifications: builder.query<{ success: boolean; data: { enabled: boolean } }, void>({
      query: () => '/admin/email-notifications/global',
      providesTags: ['GlobalEmailSettings'],
    }),

    getEmailNotificationStats: builder.query<{
      success: boolean;
      data: {
        subscribedUsers: number;
        activeUsers: number;
        lastDigestTime: string | null;
        lastBatchId: string | null;
        emailsSentToday: number;
      };
    }, void>({
      query: () => '/admin/email-notifications/stats',
      providesTags: ['GlobalEmailSettings'],
    }),

    // API Key endpoints
    getApiKeys: builder.query<ApiKey[], void>({
      query: () => '/api-keys',
      providesTags: ['ApiKey'],
    }),

    createApiKey: builder.mutation<{ key: string; api_key: ApiKey }, { key_name: string; permissions: any }>({
      query: (body) => ({
        url: '/api-keys',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ApiKey'],
    }),

    deleteApiKey: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api-keys/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ApiKey'],
    }),

    // Notification endpoints
    getNotifications: builder.query<Notification[], void>({
      query: () => '/notifications',
      providesTags: ['Notification'],
    }),

    markNotificationRead: builder.mutation<void, number>({
      query: (id) => ({
        url: `/notifications/${id}/read`,
        method: 'POST',
      }),
      invalidatesTags: ['Notification'],
    }),

    markAllNotificationsRead: builder.mutation<void, void>({
      query: () => ({
        url: '/notifications/read-all',
        method: 'POST',
      }),
      invalidatesTags: ['Notification'],
    }),

    // Admin: Version management endpoints
    createVersion: builder.mutation<{ id: number; version_number: string; software_id: number }, { softwareId: number; version: Partial<SoftwareVersion> }>({
      query: ({ softwareId, version }) => ({
        url: `/admin/software/${softwareId}/versions`,
        method: 'POST',
        body: version,
      }),
      invalidatesTags: (_result, _error, { softwareId }) => [
        { type: 'Software', id: softwareId },
        'Version',
      ],
    }),

    updateVersion: builder.mutation<void, { versionId: number; version: Partial<SoftwareVersion> }>({
      query: ({ versionId, version }) => ({
        url: `/admin/versions/${versionId}`,
        method: 'PUT',
        body: version,
      }),
      invalidatesTags: (_result, _error, { versionId }) => [
        { type: 'Version', id: versionId },
        'Software',
      ],
    }),

    deleteVersion: builder.mutation<void, number>({
      query: (versionId) => ({
        url: `/admin/versions/${versionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Version', 'Software'],
    }),

    // Admin: CVE association endpoints
    fetchCVEFromNVD: builder.query<{
      cve_id: string;
      description: string;
      cvss_score: number | null;
      severity: string | null;
      published_date: string;
      last_modified_date: string;
      references: Array<{ url: string; source: string; tags: string[] }>;
    }, string>({
      query: (cveId) => `/admin/cves/${cveId}/fetch`,
    }),

    associateCVEWithVersion: builder.mutation<void, { versionId: number; cve_id: string; status: 'affected' | 'fixed' }>({
      query: ({ versionId, cve_id, status }) => ({
        url: `/admin/versions/${versionId}/cves`,
        method: 'POST',
        body: { cve_id, status },
      }),
      invalidatesTags: (_result, _error, { versionId }) => [
        { type: 'Version', id: versionId },
        'CVE',
        'Software',
      ],
    }),

    removeCVEFromVersion: builder.mutation<void, { versionId: number; cve_id: string }>({
      query: ({ versionId, cve_id }) => ({
        url: `/admin/versions/${versionId}/cves/${cve_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { versionId }) => [
        { type: 'Version', id: versionId },
        'CVE',
        'Software',
      ],
    }),

    refreshCVEFromNVD: builder.mutation<{
      cve_id: string;
      description: string;
      cvss_score: number | null;
      severity: string | null;
      published_date: string;
      last_modified_date: string;
      enrichment_queued: boolean;
    }, string>({
      query: (cveId) => ({
        url: `/admin/cves/${cveId}/refresh`,
        method: 'POST',
      }),
      invalidatesTags: () => [
        'CVE',
        { type: 'Software', id: 'LIST' },
        'Version',
      ],
    }),

    reanalyzeCVEWithAI: builder.mutation<{
      success: boolean;
      message: string;
      task_id: string;
    }, string>({
      query: (cveId) => ({
        url: `/ai/reanalyze-cve/${cveId}`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, cveId) => [
        { type: 'CVE', id: cveId },
      ],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetSoftwareListQuery,
  useGetSoftwareByIdQuery,
  useGetReleaseChannelsQuery,
  useGetManufacturersQuery,
  useGetVersionsBySoftwareQuery,
  useGetGroupedVersionsBySoftwareQuery,
  useGetLatestVersionQuery,
  useGetVersionByIdQuery,
  useGetVersionDownloadUrlQuery,
  useGetCVEsBySoftwareQuery,
  useGetCVEsByVersionQuery,
  useSearchCVEsQuery,
  useGetJobsQuery,
  useGetJobByIdQuery,
  useGetJobLogsQuery,
  useTriggerJobMutation,
  useGetCurrentUserQuery,
  useUpdateUserPreferencesMutation,
  useGetApiKeysQuery,
  useCreateApiKeyMutation,
  useDeleteApiKeyMutation,
  useGetNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useCreateVersionMutation,
  useUpdateVersionMutation,
  useDeleteVersionMutation,
  useFetchCVEFromNVDQuery,
  useLazyFetchCVEFromNVDQuery,
  useAssociateCVEWithVersionMutation,
  useRemoveCVEFromVersionMutation,
  useRefreshCVEFromNVDMutation,
  useReanalyzeCVEWithAIMutation,
  useToggleEmailNotificationsMutation,
  useGetEmailPreferencesQuery,
  useToggleGlobalEmailNotificationsMutation,
  useGetGlobalEmailNotificationsQuery,
  useGetEmailNotificationStatsQuery,
} = api;
